<template>
  <AppToggleSection>
    <template #title>
      Ajout d'un domaine perso
    </template>
    <template #default>
      <form @submit.prevent="handleProvision">
        <b-field label="Nom de domaine personnalisé">
          <b-input
            v-model="domain"
            placeholder="formation.exemple.com"
            type="text"
            required
          />
        </b-field>

        <b-field>
          <b-checkbox v-model="dryRun">
            Mode simulation (dry run) - Ne fait aucune modification réelle
          </b-checkbox>
        </b-field>

        <div class="buttons">
          <b-button
            type="is-warning"
            :loading="isFetchingDomainInfo"
            :disabled="!domain || isLoading"
            @click="handleGetDomainInfo"
          >
            Voir infos du domaine
          </b-button>
          <b-button
            type="is-primary"
            native-type="submit"
            :loading="isLoading"
            :disabled="!domain"
          >
            Configurer
          </b-button>
          <b-button
            type="is-info"
            :loading="isCheckingDns"
            :disabled="!domain || isLoading"
            @click="handleDnsCheck"
          >
            Vérifier
          </b-button>
          <b-button
            type="is-success"
            :loading="isEnablingCustomDomain"
            :disabled="!domain || isLoading"
            @click="handleEnableCustomDomain"
          >
            Activer
          </b-button>
        </div>
      </form>

      <div v-if="domainInfo" class="mt-4">
        <b-message :type="domainInfoType">
          <!-- Cas 200 : domaine existant -->
          <template v-if="domainInfo.status_code === 200">
            <p><strong>Domaine existant</strong></p>
            <p>Domaine: {{ domainInfo.data.domain }}</p>
            <p v-if="domainInfo.data.enabled !== undefined">
              Activé: {{ domainInfo.data.enabled ? 'Oui' : 'Non' }}
            </p>
            <p v-if="domainInfo.data.ssl_status">SSL: {{ domainInfo.data.ssl_status }}</p>
            <p v-if="domainInfo.data.environment">Environnement: {{ domainInfo.data.environment }}</p>
            <p v-if="domainInfo.data.backend">Backend: {{ domainInfo.data.backend }}</p>
            <p class="mt-2 has-text-info">
              → Vous pouvez lancer une vérification DNS
            </p>
            <details class="mt-2">
              <summary>Données complètes</summary>
              <pre class="mt-2">
                {{ formatJson(domainInfo.data) }}
              </pre>
            </details>
          </template>

          <!-- Cas 404 : domaine non trouvé -->
          <template v-else-if="domainInfo.status_code === 404">
            <p><strong>Domaine non trouvé</strong></p>
            <p>{{ domainInfo.message }}</p>
            <p class="mt-2 has-text-warning">
              → Utilisez "Configurer le domaine" pour créer ce domaine
            </p>
          </template>

          <!-- Cas 400 : domaine invalide -->
          <template v-else-if="domainInfo.status_code === 400">
            <p><strong>Domaine invalide</strong></p>
            <p>{{ domainInfo.message }}</p>
            <p class="mt-2 has-text-danger">
              → Vérifiez le format du nom de domaine
            </p>
          </template>

          <!-- Autre erreur -->
          <template v-else>
            <p><strong>Erreur</strong></p>
            <p>{{ domainInfo.message || 'Erreur inconnue' }}</p>
          </template>
        </b-message>
      </div>

      <div v-if="jobResult" class="mt-4">
        <b-message :type="jobResultType">
          <p><strong>Job #{{ jobResult.id }}</strong> - Status: {{ jobResult.status }}</p>
          <p v-if="jobResult.domain">Domaine: {{ jobResult.domain }}</p>
          <p v-if="jobResult.error" class="has-text-danger">Erreur: {{ jobResult.error }}</p>
          <div class="mt-2">
            <b-button size="is-small" :loading="isPolling" @click="pollJobStatus">
              Rafraîchir le statut
            </b-button>
          </div>
          <details v-if="jobResult.result_json" class="mt-2">
            <summary>Détails du résultat</summary>
            <pre class="mt-2">
              {{ formatJson(jobResult.result_json) }}
            </pre>
          </details>
        </b-message>
      </div>

      <div v-if="dnsResult" class="mt-4">
        <b-message :type="dnsResultType">
          <p><strong>Vérification DNS - Job #{{ dnsResult.id }}</strong></p>
          <p>Status: {{ dnsResult.status }}</p>
          <div class="mt-2">
            <b-button size="is-small" :loading="isPollingDns" @click="pollDnsStatus">
              Rafraîchir le statut
            </b-button>
          </div>
          <details v-if="dnsResult.result_json" class="mt-2">
            <summary>Détails DNS</summary>
            <pre class="mt-2">
              {{ formatJson(dnsResult.result_json) }}
            </pre>
          </details>
        </b-message>
      </div>

      <div v-if="enableResult" class="mt-4">
        <b-message type="is-success">
          <p><strong>Domaine personnalisé activé</strong></p>
          <p>Sous domaine : {{ enableResult.subdomain }}.teachizy.fr</p>
          <p>Domaine perso : {{ enableResult.custom_domain }}</p>
          <p>Forfait : {{ enableResult.plan }}</p>
        </b-message>
      </div>
    </template>
  </AppToggleSection>
</template>

<script>
import APIAdmin from '@app/services/API/Admin';

export default {
  name: 'DomainProvision',
  data() {
    return {
      domain: '',
      dryRun: false,
      isLoading: false,
      isCheckingDns: false,
      isPolling: false,
      isPollingDns: false,
      isFetchingDomainInfo: false,
      isEnablingCustomDomain: false,
      jobResult: null,
      dnsResult: null,
      domainInfo: null,
      enableResult: null,
    };
  },
  computed: {
    jobResultType() {
      if (!this.jobResult) return 'is-info';
      switch (this.jobResult.status) {
        case 'success': return 'is-success';
        case 'failed': return 'is-danger';
        case 'running': return 'is-warning';
        case 'queued': return 'is-info';
        default: return 'is-info';
      }
    },
    dnsResultType() {
      if (!this.dnsResult) return 'is-info';
      switch (this.dnsResult.status) {
        case 'success': return 'is-success';
        case 'failed': return 'is-danger';
        default: return 'is-info';
      }
    },
    domainInfoType() {
      if (!this.domainInfo) return 'is-info';
      switch (this.domainInfo.status_code) {
        case 200: return 'is-success';
        case 404: return 'is-warning';
        case 400: return 'is-danger';
        default: return 'is-info';
      }
    },
  },
  methods: {
    async handleGetDomainInfo() {
      this.isFetchingDomainInfo = true;
      this.domainInfo = null;

      try {
        const { data } = await APIAdmin.getDomainStatus(this.domain);
        this.domainInfo = data;

        const toastConfig = {
          200: { message: 'Domaine existant - Vous pouvez vérifier le DNS', type: 'is-success' },
          404: { message: 'Domaine non trouvé - Provisionnement nécessaire', type: 'is-warning' },
          400: { message: 'Nom de domaine invalide', type: 'is-danger' },
        };

        const config = toastConfig[data.status_code] || { message: 'Réponse reçue', type: 'is-info' };
        this.$buefy.toast.open(config);
      } catch (error) {
        console.error('Erreur lors de la récupération des informations:', error);
        this.$buefy.toast.open({
          message: error.response?.data?.message || 'Erreur lors de la récupération des informations du domaine',
          type: 'is-danger',
        });
      } finally {
        this.isFetchingDomainInfo = false;
      }
    },
    formatJson(jsonString) {
      try {
        const parsed = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
        return JSON.stringify(parsed, null, 2);
      } catch {
        return jsonString;
      }
    },
    async handleProvision() {
      this.isLoading = true;
      this.jobResult = null;

      try {
        const { data } = await APIAdmin.provisionDomain({
          domain: this.domain,
          dry_run: this.dryRun,
        });
        this.jobResult = data;

        if (data.status === 'success') {
          this.$buefy.toast.open({
            message: `Domaine ${this.domain} provisionné avec succès !`,
            type: 'is-success',
          });
        } else if (data.status === 'running' || data.status === 'queued') {
          this.$buefy.toast.open({
            message: `Job de provisionnement créé (ID: ${data.id})`,
            type: 'is-info',
          });
        }
      } catch (error) {
        console.error('Erreur lors du provisionnement:', error);
        this.$buefy.toast.open({
          message: error.response?.data?.message || 'Erreur lors du provisionnement du domaine',
          type: 'is-danger',
        });
      } finally {
        this.isLoading = false;
      }
    },
    async handleDnsCheck() {
      this.isCheckingDns = true;
      this.dnsResult = null;

      try {
        const { data } = await APIAdmin.checkDns(this.domain);
        this.dnsResult = data;

        this.$buefy.toast.open({
          message: 'Vérification DNS lancée',
          type: 'is-info',
        });
      } catch (error) {
        console.error('Erreur lors de la vérification DNS:', error);
        this.$buefy.toast.open({
          message: error.response?.data?.message || 'Erreur lors de la vérification DNS',
          type: 'is-danger',
        });
      } finally {
        this.isCheckingDns = false;
      }
    },
    async pollJobStatus() {
      if (!this.jobResult?.id) return;

      this.isPolling = true;

      try {
        const { data } = await APIAdmin.getJobStatus(this.jobResult.id);
        this.jobResult = data;
      } catch (error) {
        console.error('Erreur lors de la récupération du statut:', error);
      } finally {
        this.isPolling = false;
      }
    },
    async pollDnsStatus() {
      if (!this.dnsResult?.id) return;

      this.isPollingDns = true;

      try {
        const { data } = await APIAdmin.getJobStatus(this.dnsResult.id);
        this.dnsResult = data;
      } catch (error) {
        console.error('Erreur lors de la récupération du statut DNS:', error);
      } finally {
        this.isPollingDns = false;
      }
    },
    async handleEnableCustomDomain() {
      this.isEnablingCustomDomain = true;
      this.enableResult = null;

      try {
        const { data } = await APIAdmin.enableCustomDomain({
          custom_domain: this.domain,
        });
        this.enableResult = data;

        this.$buefy.toast.open({
          message: `Domaine personnalisé activé pour ${data.subdomain}.teachizy.fr`,
          type: 'is-success',
        });
      } catch (error) {
        console.error('Erreur lors de l\'activation:', error);
        this.$buefy.toast.open({
          message: error.response?.data?.message || 'Erreur lors de l\'activation du domaine personnalisé',
          type: 'is-danger',
        });
      } finally {
        this.isEnablingCustomDomain = false;
      }
    },
  },
};
</script>

<style scoped>
pre {
  background: #f5f5f5;
  padding: 1rem;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 0.85rem;
}
details summary {
  cursor: pointer;
  color: #3273dc;
}
</style>
